/**
 * Connector health and freshness contracts (BUILD-PLAN 2.5.2, 2.5.6, M02,
 * WP1-HEALTH).
 *
 * Rule from the McLaren 13.5-day silent stall: **alarm on the age of the last
 * successful read or write, not on error rate** (BUILD-PLAN 2.5.6). Every
 * freshness value is therefore computed from `lastSuccessAt`; errors only feed
 * the root-cause hint.
 */
import { z } from 'zod';
import {
  EntityNameSchema,
  IdSchema,
  InstantSchema,
  LocationOwnedSchema,
  PmsAdapterSchema,
  PmsSystemSchema,
} from './sync.js';

/* ------------------------------------------------------------------------ */
/* Freshness                                                                 */
/* ------------------------------------------------------------------------ */

/**
 * Freshness SLO per mirrored entity, in seconds. `appointment` (60 s) and
 * `patient` (5 min) come from the S0 exit criteria (BUILD-PLAN 1.2). The rest
 * are Velora defaults for the v1 poll tiers and are overridable per office;
 * PulseSync v1 today delivers 2 h to 24 h (BUILD-PLAN 2.5.2), so v1 offices
 * will show amber until v2 change cursors land.
 */
export const DEFAULT_FRESHNESS_SLO_SECONDS: Readonly<Record<string, number>> = {
  appointment: 60,
  patient: 300,
  contact_point: 300,
  household: 300,
  recall: 900,
  procedure: 900,
  provider: 3600,
  operatory: 3600,
  appointment_type: 3600,
  blockout: 900,
};

/** SLO for an entity not in the table. */
export const FALLBACK_FRESHNESS_SLO_SECONDS = 900;

/** Amber at 2x SLO, red at 5x SLO (M02 acceptance). */
export const FRESHNESS_AMBER_FACTOR = 2;
export const FRESHNESS_RED_FACTOR = 5;

/**
 * - `fresh`: age below 2x SLO.
 * - `amber`: 2x to 5x SLO.
 * - `red`: 5x SLO or more; fires the alarm.
 * - `never`: no successful sync yet; treated as red for alarms.
 */
export const FRESHNESS_LEVELS = ['fresh', 'amber', 'red', 'never'] as const;
export const FreshnessLevelSchema = z.enum(FRESHNESS_LEVELS);
export type FreshnessLevel = z.infer<typeof FreshnessLevelSchema>;

/** Freshness level for an age (seconds since last success) and SLO. */
export function freshnessLevel(ageSeconds: number | null, sloSeconds: number): FreshnessLevel {
  // Fail closed: a NaN or infinite age (unparseable timestamp) must alarm,
  // never read as fresh (BUILD-PLAN 2.5.6, McLaren silent-stall lesson).
  if (ageSeconds === null || !Number.isFinite(ageSeconds)) return 'never';
  if (ageSeconds >= sloSeconds * FRESHNESS_RED_FACTOR) return 'red';
  if (ageSeconds >= sloSeconds * FRESHNESS_AMBER_FACTOR) return 'amber';
  return 'fresh';
}

const LEVEL_RANK: Readonly<Record<FreshnessLevel, number>> = {
  fresh: 0,
  amber: 1,
  red: 2,
  never: 3,
};

/** The worst level in a set (for a screen header that covers several entities). */
export function worstFreshness(levels: readonly FreshnessLevel[]): FreshnessLevel {
  return levels.reduce<FreshnessLevel>(
    (worst, l) => (LEVEL_RANK[l] > LEVEL_RANK[worst] ? l : worst),
    'fresh',
  );
}

/** True when a level should raise the freshness alarm (ntfy, no PHI). */
export function isFreshnessAlarm(level: FreshnessLevel): boolean {
  return level === 'red' || level === 'never';
}

/** Freshness of one mirrored entity at one office. */
export const FreshnessSchema = z
  .object({
    ...LocationOwnedSchema.shape,
    entity: EntityNameSchema,
    lastSuccessAt: InstantSchema.nullable(),
    lastAttemptAt: InstantSchema.nullable(),
    /** Seconds since `lastSuccessAt` at `computedAt`; null when never synced. */
    ageSeconds: z.number().int().nonnegative().nullable(),
    sloSeconds: z.number().int().positive(),
    level: FreshnessLevelSchema,
    computedAt: InstantSchema,
  })
  .superRefine((f, ctx) => {
    if ((f.lastSuccessAt === null) !== (f.ageSeconds === null)) {
      ctx.addIssue({
        code: 'custom',
        path: ['ageSeconds'],
        message: 'ageSeconds is null exactly when there was no successful sync',
      });
    }
    const expected = freshnessLevel(f.ageSeconds, f.sloSeconds);
    if (f.level !== expected) {
      ctx.addIssue({
        code: 'custom',
        path: ['level'],
        message: `level must be "${expected}" for age ${String(f.ageSeconds)} s and SLO ${f.sloSeconds} s`,
      });
    }
  });
export type Freshness = z.infer<typeof FreshnessSchema>;

/** Build a Freshness value; keeps `ageSeconds` and `level` consistent. */
export function computeFreshness(input: {
  practiceId: string;
  locationId: string;
  entity: string;
  lastSuccessAt: string | null;
  lastAttemptAt: string | null;
  sloSeconds?: number;
  now: Date;
}): Freshness {
  const slo =
    input.sloSeconds ??
    (Object.prototype.hasOwnProperty.call(DEFAULT_FRESHNESS_SLO_SECONDS, input.entity)
      ? DEFAULT_FRESHNESS_SLO_SECONDS[input.entity]
      : undefined) ??
    FALLBACK_FRESHNESS_SLO_SECONDS;
  const lastSuccessMs = input.lastSuccessAt === null ? null : Date.parse(input.lastSuccessAt);
  // An unparseable lastSuccessAt is treated as no successful sync (alarm).
  const ageSeconds =
    lastSuccessMs === null || !Number.isFinite(lastSuccessMs)
      ? null
      : Math.max(0, Math.floor((input.now.getTime() - lastSuccessMs) / 1000));
  return {
    practiceId: input.practiceId,
    locationId: input.locationId,
    entity: input.entity,
    lastSuccessAt: ageSeconds === null ? null : input.lastSuccessAt,
    lastAttemptAt: input.lastAttemptAt,
    ageSeconds,
    sloSeconds: slo,
    level: freshnessLevel(ageSeconds, slo),
    computedAt: input.now.toISOString(),
  };
}

/* ------------------------------------------------------------------------ */
/* Connector health (BUILD-PLAN 2.5.6, Atlas Systems/diagnose pattern)       */
/* ------------------------------------------------------------------------ */

export const ConnectorStatusSchema = z.enum(['online', 'degraded', 'offline', 'unknown']);
export type ConnectorStatus = z.infer<typeof ConnectorStatusSchema>;

/** Root-cause hints shown on the Connector Health panel (BUILD-PLAN 2.5.6). */
export const ROOT_CAUSE_HINTS = [
  'none',
  'dns',
  'tls',
  'connector_stalled',
  'connector_offline',
  'pms_version_changed',
  'pms_unreachable',
  'auth',
  'rate_limited',
  'unknown',
] as const;
export const RootCauseHintSchema = z.enum(ROOT_CAUSE_HINTS);
export type RootCauseHint = z.infer<typeof RootCauseHintSchema>;

/** Body of `GET /v1/sync/connectors/:locationId` (`health` field); WP1-HEALTH renders it. */
export const ConnectorHealthSchema = z.object({
  ...LocationOwnedSchema.shape,
  pmsLinkId: IdSchema,
  adapter: PmsAdapterSchema,
  adapterVersion: z.string().nullable(),
  pmsSystem: PmsSystemSchema,
  pmsVersion: z.string().nullable(),
  /** Set when the PMS version changed; writes pause until re-verified. */
  pmsVersionChangedAt: InstantSchema.nullable(),
  status: ConnectorStatusSchema,
  lastHeartbeatAt: InstantSchema.nullable(),
  entities: z.array(FreshnessSchema),
  /** Commands queued, dispatched or sent. */
  queueDepth: z.number().int().nonnegative(),
  oldestPendingCommandAgeSeconds: z.number().int().nonnegative().nullable(),
  unknownCommandCount: z.number().int().nonnegative(),
  openConflictCount: z.number().int().nonnegative(),
  writesPaused: z.boolean(),
  rootCause: RootCauseHintSchema,
  /** One line for staff; no PHI, no secrets. */
  rootCauseDetail: z.string().max(300).nullable(),
  checkedAt: InstantSchema,
});
export type ConnectorHealth = z.infer<typeof ConnectorHealthSchema>;

/* ------------------------------------------------------------------------ */
/* Parity (BUILD-PLAN M02, 1.3 Gate C)                                       */
/* ------------------------------------------------------------------------ */

/**
 * Row counts and field checksums per entity per office. Gate C thresholds:
 * drift below 0.1% for clinical classes and 0 for money.
 */
export const ParityReportSchema = z
  .object({
    id: IdSchema,
    ...LocationOwnedSchema.shape,
    pmsLinkId: IdSchema,
    entity: EntityNameSchema,
    computedAt: InstantSchema,
    veloraCount: z.number().int().nonnegative(),
    pmsCount: z.number().int().nonnegative(),
    veloraChecksum: z.string().nullable(),
    pmsChecksum: z.string().nullable(),
    /**
     * Rows that differ, including rows present on only one side, so it is
     * never less than |veloraCount - pmsCount|.
     */
    mismatchedRows: z.number().int().nonnegative(),
    /** min(1, mismatchedRows / max(pmsCount, 1)); see `parityDriftRatio`. */
    driftRatio: z.number().min(0).max(1),
    threshold: z.number().min(0).max(1),
    flagged: z.boolean(),
  })
  .superRefine((p, ctx) => {
    const missing = Math.abs(p.veloraCount - p.pmsCount);
    if (p.mismatchedRows < missing) {
      ctx.addIssue({
        code: 'custom',
        path: ['mismatchedRows'],
        message: `mismatchedRows must count the ${missing} rows missing on one side`,
      });
    }
    if (
      p.mismatchedRows === 0 &&
      p.veloraChecksum !== null &&
      p.pmsChecksum !== null &&
      p.veloraChecksum !== p.pmsChecksum
    ) {
      ctx.addIssue({
        code: 'custom',
        path: ['mismatchedRows'],
        message: 'checksums differ, so at least one row differs',
      });
    }
    const expected = parityDriftRatio(p.mismatchedRows, p.pmsCount);
    if (Math.abs(p.driftRatio - expected) > 1e-12) {
      ctx.addIssue({
        code: 'custom',
        path: ['driftRatio'],
        message: `driftRatio must be ${String(expected)}`,
      });
    }
    if (p.flagged !== p.driftRatio > p.threshold) {
      ctx.addIssue({
        code: 'custom',
        path: ['flagged'],
        message: 'flagged must equal driftRatio > threshold',
      });
    }
  });
export type ParityReport = z.infer<typeof ParityReportSchema>;

/** Drift ratio of a parity run: min(1, mismatchedRows / max(pmsCount, 1)). */
export function parityDriftRatio(mismatchedRows: number, pmsCount: number): number {
  return Math.min(1, mismatchedRows / Math.max(pmsCount, 1));
}

/** Gate C drift thresholds (BUILD-PLAN 1.3). */
export const PARITY_THRESHOLDS = { clinical: 0.001, money: 0 } as const;

/* ------------------------------------------------------------------------ */
/* One freshness rule (V06: ADM-04, HA-08, LED-11, DAN-F-13, P1-01)          */
/* ------------------------------------------------------------------------ */

/**
 * THE freshness rule. Every screen uses it; nothing else computes freshness.
 *
 * 1. An entity's freshness is the age of its last successful read against
 *    its own SLO (`syncSloSeconds`): amber at 2x, red at 5x.
 * 2. A KPI, chip or card is as fresh as its OLDEST SOURCE ENTITY
 *    (`kpiFreshness`): Production reads procedures, the schedule reads
 *    appointments, recall lists read recalls, claims read claims. Never the
 *    minimum or maximum over every entity of the office (daily carrier pulls
 *    say nothing about today's production).
 * 3. An office is Degraded only when its core entities (appointments or
 *    patients) are amber or worse (age >= 2x SLO). Slow reference data
 *    (carriers, codes) never marks an office Degraded.
 * 4. A feed that is not synced at all (see `dataAvailability` in sync.ts) has
 *    no freshness; the screen says "Not synced from <PMS>" instead.
 */
export const CORE_FRESHNESS_ENTITIES = ['appointments', 'patients'] as const;

/** Sync SLO (seconds) per PMS entity for PulseSync v1 links (2026-10-14 tiers). */
export const V1_SYNC_SLO_SECONDS: Readonly<Record<string, number>> = {
  // Polled every 60 s incrementally; the connector itself lands PMS changes
  // every few minutes and a live request costs 10-60 s, so 5 min is honest.
  appointments: 300,
  patients: 600,
  // Today's procedures every 5 min (`from` = yesterday), full read every 2 h.
  procedures: 600,
  claims: 1800,
  recalls: 3600,
  treatment_plans: 21_600,
  planned_services: 21_600,
  providers: 21_600,
  operatories: 21_600,
  procedure_codes: 86_400,
  carriers: 86_400,
  employers: 86_400,
};

/** Sync SLO (seconds) for incremental adapters (mock, PulseSync v2). */
export const INCREMENTAL_SYNC_SLO_SECONDS: Readonly<Record<string, number>> = {
  appointments: 60,
  patients: 300,
  procedures: 300,
  claims: 900,
  recalls: 900,
  treatment_plans: 900,
  planned_services: 900,
  providers: 3600,
  operatories: 3600,
  procedure_codes: 21_600,
  carriers: 21_600,
  employers: 21_600,
};

/** The SLO of one entity at one link. */
export function syncSloSeconds(adapter: string, entity: string): number {
  const table = adapter === 'pulsesync-v1' ? V1_SYNC_SLO_SECONDS : INCREMENTAL_SYNC_SLO_SECONDS;
  return Object.prototype.hasOwnProperty.call(table, entity)
    ? (table[entity] ?? FALLBACK_FRESHNESS_SLO_SECONDS)
    : FALLBACK_FRESHNESS_SLO_SECONDS;
}

/** Freshness of a KPI: the oldest of its source entities (rule 2). */
export function kpiFreshness(sources: readonly Pick<Freshness, 'ageSeconds' | 'level'>[]): {
  ageSeconds: number | null;
  level: FreshnessLevel;
} {
  if (sources.length === 0) return { ageSeconds: null, level: 'never' };
  const level = worstFreshness(sources.map((s) => s.level));
  const ages = sources.map((s) => s.ageSeconds);
  const ageSeconds = ages.some((a) => a === null) ? null : Math.max(...ages.map((a) => a ?? 0));
  return { ageSeconds, level };
}

/** True when an office counts as Degraded by freshness (rule 3). */
export function isOfficeDegraded(
  entities: readonly Pick<Freshness, 'entity' | 'level'>[],
): boolean {
  return entities.some(
    (e) => (CORE_FRESHNESS_ENTITIES as readonly string[]).includes(e.entity) && e.level !== 'fresh',
  );
}

/**
 * Stalled cursor (DTX-04, PT-07, OD-10): appointments with no successful
 * read for this long raise an alarm even when the connector looks online.
 */
export const STALLED_APPOINTMENTS_SECONDS = 15 * 60;
/** The nightly parity job counts as missed after this long (ADM-07). */
export const PARITY_STALE_SECONDS = 26 * 3600;

export const SYNC_ALARM_CODES = [
  'appointments_stalled',
  'parity_stale',
  'rows_skipped',
  'negative_fee_clamped',
  'tombstone_guard',
  // Patient status changes the sync held (2026-10-16 status guard).
  'patient_status_held',
] as const;
export const SyncAlarmCodeSchema = z.enum(SYNC_ALARM_CODES);
export type SyncAlarmCode = z.infer<typeof SyncAlarmCodeSchema>;

/** An alarm on the Connector Health panel. Counts and codes only, no PHI. */
export const SyncAlarmSchema = z.object({
  code: SyncAlarmCodeSchema,
  level: z.enum(['warn', 'error']),
  message: z.string().max(300),
  since: InstantSchema.nullable(),
});
export type SyncAlarm = z.infer<typeof SyncAlarmSchema>;

/**
 * Per-run issue aggregate of the latest run per entity (DAP-F7, PROC-07,
 * DTP-TX-13): the true number of rows per issue code, not the 500 stored
 * examples. `{ procedures: { missing_code: 25012, negative_fee: 194 } }`.
 */
export const IssueCountsSchema = z.record(z.string(), z.record(z.string(), z.number().int()));
export type IssueCounts = z.infer<typeof IssueCountsSchema>;

/**
 * Display-only capability summary (ADM-06): what the dispatcher actually
 * computes for this office (v1 allow-list under the server's write policy,
 * verifications, pauses). It never gates anything; gating stays in the
 * dispatcher.
 */
export const ReportedCapabilitiesSchema = z.object({
  source: z.enum(['computed_v1_policy', 'stored_manifest', 'none']),
  writePolicy: z.string().nullable(),
  commands: z.array(
    z.object({
      type: z.string(),
      status: z.enum(['stable', 'experimental', 'blocked', 'unsupported']),
      usableNow: z.boolean(),
    }),
  ),
  usableNow: z.number().int().nonnegative(),
  total: z.number().int().nonnegative(),
  computedAt: InstantSchema,
});
export type ReportedCapabilities = z.infer<typeof ReportedCapabilitiesSchema>;

/** Body of `GET /v1/sync/freshness?entities=procedures,appointments`. */
export const SyncFreshnessResponseSchema = z.object({
  offices: z.array(
    z.object({
      locationId: IdSchema,
      entities: z.array(FreshnessSchema),
      /** Rule 2 over the requested entities. */
      kpi: z.object({
        ageSeconds: z.number().int().nonnegative().nullable(),
        level: FreshnessLevelSchema,
      }),
    }),
  ),
  computedAt: InstantSchema,
});
export type SyncFreshnessResponse = z.infer<typeof SyncFreshnessResponseSchema>;

/* ------------------------------------------------------------------------ */
/* Sync API bodies (WP1-SYNC; consumed by WP1-HEALTH and WP4-MIGRATE)        */
/* ------------------------------------------------------------------------ */

/** One office in `GET /v1/sync/connectors` (the Settings PulseSync table). */
export const ConnectorSummarySchema = z.object({
  ...LocationOwnedSchema.shape,
  pmsLinkId: IdSchema,
  adapter: PmsAdapterSchema,
  pmsSystem: PmsSystemSchema,
  pmsVersion: z.string().nullable(),
  adapterVersion: z.string().nullable(),
  status: ConnectorStatusSchema,
  /** Oldest last-success across entities: the age the office header shows. */
  lastSyncAt: InstantSchema.nullable(),
  ageSeconds: z.number().int().nonnegative().nullable(),
  /** Worst entity level for the office. */
  freshness: FreshnessLevelSchema,
  queueDepth: z.number().int().nonnegative(),
  unknownCommandCount: z.number().int().nonnegative(),
  openConflictCount: z.number().int().nonnegative(),
  openIssueCount: z.number().int().nonnegative(),
  writesPaused: z.boolean(),
  writesPausedReason: z.string().nullable(),
  rootCause: RootCauseHintSchema,
  rootCauseDetail: z.string().max(300).nullable(),
  /** Stalled appointments, missed parity, skipped rows (V06). */
  alarms: z.array(SyncAlarmSchema).optional(),
});
export type ConnectorSummary = z.infer<typeof ConnectorSummarySchema>;

/** Body of `GET /v1/sync/connectors`. Totals are over the offices in scope. */
export const ConnectorListSchema = z.object({
  offices: z.array(ConnectorSummarySchema),
  totals: z.object({
    linked: z.number().int().nonnegative(),
    online: z.number().int().nonnegative(),
    /** Median office age (seconds since last successful read); null if none synced. */
    medianAgeSeconds: z.number().int().nonnegative().nullable(),
    queueDepth: z.number().int().nonnegative(),
    unknownCommandCount: z.number().int().nonnegative(),
    openConflictCount: z.number().int().nonnegative(),
  }),
  computedAt: InstantSchema,
});
export type ConnectorList = z.infer<typeof ConnectorListSchema>;

/** One bar of the "data age, last 2 hours" strip. */
export const FreshnessSampleSchema = z.object({
  at: InstantSchema,
  /** Age of the office's oldest entity at that time, seconds. */
  ageSeconds: z.number().int().nonnegative(),
  level: FreshnessLevelSchema,
});
export type FreshnessSample = z.infer<typeof FreshnessSampleSchema>;

/** A row the adapter could not take (ids and codes only, never PHI). */
export const SyncIssueSchema = z.object({
  id: IdSchema,
  entity: EntityNameSchema,
  pmsId: z.string(),
  code: z.string(),
  message: z.string().max(300),
  occurrences: z.number().int().positive(),
  firstSeenAt: InstantSchema,
  lastSeenAt: InstantSchema,
});
export type SyncIssue = z.infer<typeof SyncIssueSchema>;

/** Body of `GET /v1/sync/connectors/:locationId`. */
/**
 * The sync worker's today fast lane for one office: today's and tomorrow's
 * appointments every `intervalMs` (counts and times only).
 */
export const FastLaneStatusSchema = z.object({
  intervalMs: z.number().int().nonnegative(),
  /** When the last tick started. */
  lastTickAt: InstantSchema,
  lastOkAt: InstantSchema.nullable(),
  /** Duration of the last tick. */
  ms: z.number().int().nonnegative(),
  /** Changed rows the PMS returned / rows Velora wrote, last tick. */
  rows: z.number().int().nonnegative(),
  written: z.number().int().nonnegative(),
  /** One line for staff; no PHI. */
  error: z.string().max(300).nullable(),
});
export type FastLaneStatus = z.infer<typeof FastLaneStatusSchema>;

export const ConnectorDetailSchema = z.object({
  health: ConnectorHealthSchema,
  /** Mirrored rows per entity (not deleted). */
  rowsMirrored: z.record(z.string(), z.number().int().nonnegative()),
  history: z.array(FreshnessSampleSchema),
  issues: z.array(SyncIssueSchema),
  sorStage: z.string().nullable(),
  /** True row counts per issue code of the latest run per entity (V06). */
  issueCounts: IssueCountsSchema.optional(),
  alarms: z.array(SyncAlarmSchema).optional(),
  /** Display only; never gates (ADM-06). */
  reportedCapabilities: ReportedCapabilitiesSchema.nullable().optional(),
  /** The worker's today/tomorrow appointment poll; null before its first tick. */
  fastLane: FastLaneStatusSchema.nullable().optional(),
});
export type ConnectorDetail = z.infer<typeof ConnectorDetailSchema>;

/** Body of `POST /v1/sync/connectors/:locationId/test`. */
export const ConnectorTestResultSchema = z.object({
  ok: z.boolean(),
  status: ConnectorStatusSchema,
  roundTripMs: z.number().int().nonnegative(),
  pmsVersion: z.string().nullable(),
  adapterVersion: z.string().nullable(),
  rootCause: RootCauseHintSchema,
  /** One line for staff; no PHI, never the key. */
  detail: z.string().max(300),
  checkedAt: InstantSchema,
});
export type ConnectorTestResult = z.infer<typeof ConnectorTestResultSchema>;

export const SyncLogLevelSchema = z.enum(['ok', 'info', 'warn', 'error']);
/** One line of the connector sync log. Ids, counts and codes only. */
export const SyncLogEntrySchema = z.object({
  at: InstantSchema,
  level: SyncLogLevelSchema,
  kind: z.enum(['sync', 'command', 'parity', 'connector']),
  text: z.string().max(300),
});
export type SyncLogEntry = z.infer<typeof SyncLogEntrySchema>;

/** Body of `GET /v1/sync/connectors/:locationId/log`. */
export const SyncLogSchema = z.object({ entries: z.array(SyncLogEntrySchema) });
export type SyncLog = z.infer<typeof SyncLogSchema>;

/** Body of `GET /v1/sync/parity`: latest report per entity per office, plus a 30-day drift trend. */
export const ParityOverviewSchema = z.object({
  reports: z.array(ParityReportSchema),
  trend: z.array(
    z.object({
      locationId: IdSchema,
      entity: EntityNameSchema,
      points: z.array(z.object({ day: z.iso.date(), driftRatio: z.number().min(0).max(1) })),
    }),
  ),
});
export type ParityOverview = z.infer<typeof ParityOverviewSchema>;

/**
 * Body of `GET /v1/sync/connectors/:locationId/capability-view`: the
 * structural capability view `@velora/domain/migration` takes for Gate C and
 * the per-class command gate (status per command, restrictions, and
 * verifications pinned to a PMS version).
 */
export const CapabilityViewSchema = z.object({
  pmsSystem: PmsSystemSchema,
  entries: z.array(
    z.object({
      command: z.string(),
      status: z.enum(['stable', 'experimental', 'blocked']),
      restrictions: z.array(z.string()),
      verifications: z.array(
        z.object({
          locationId: IdSchema,
          pmsVersion: z.string(),
          verifiedAt: InstantSchema,
          verifiedBy: z.string(),
          method: z.enum(['sandbox_write_readback', 'fault_injection', 'pilot_readback']),
        }),
      ),
    }),
  ),
});
export type CapabilityView = z.infer<typeof CapabilityViewSchema>;
